import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RouteFallback } from '@/shared/components/RouteFallback';
import { LoginPage } from '../auth/LoginPage';
import { RequireAuth } from '../auth/RequireAuth';
import { AppShell } from '../layout/AppShell';
import { RequireRole } from './RequireRole';
import { RouteError } from './RouteError';

const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, key: K) =>
  lazy(() => load().then((m) => ({ default: m[key] })));

const WhatsNewPage = named(() => import('@/features/whats-new'), 'WhatsNewPage');
const LibraryPage = named(() => import('@/features/library'), 'LibraryPage');
const DocumentPage = named(() => import('@/features/documents'), 'DocumentPage');
const AskPage = named(() => import('@/features/assistant'), 'AskPage');
const HouseViewsPage = named(() => import('@/features/house-views'), 'HouseViewsPage');
const ForecastsPage = named(() => import('@/features/forecasts'), 'ForecastsPage');
const IngestionPage = named(() => import('@/features/ingestion'), 'IngestionPage');
const ApprovalsPage = named(() => import('@/features/approvals'), 'ApprovalsPage');
const AuditPage = named(() => import('@/features/audit'), 'AuditPage');
const SubscribersPage = named(() => import('@/features/subscribers'), 'SubscribersPage');
const MyListsPage = named(() => import('@/features/subscribers'), 'MyListsPage');
const UsersPage = named(() => import('@/features/users'), 'UsersPage');
const RmListsPage = named(() => import('@/features/users'), 'RmListsPage');
const DeskPage = named(() => import('@/features/desk'), 'DeskPage');
const FeedbackPage = named(() => import('@/features/feedback'), 'FeedbackPage');

const page = (Component: ComponentType, path: string) => (
  <RequireRole path={path}>
    <Suspense fallback={<RouteFallback />}>
      <Component />
    </Suspense>
  </RequireRole>
);

export const router = createBrowserRouter([
  { path: 'login', element: <LoginPage />, errorElement: <RouteError /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: page(AskPage, '/') },
      { path: 'whats-new', element: page(WhatsNewPage, '/whats-new') },
      { path: 'library', element: page(LibraryPage, '/library') },
      { path: 'documents/:documentId', element: page(DocumentPage, '/documents') },
      { path: 'ask', element: <Navigate to="/" replace /> }, // old links and bookmarks
      { path: 'house-views', element: page(HouseViewsPage, '/house-views') },
      { path: 'forecasts', element: page(ForecastsPage, '/forecasts') },
      { path: 'ingestion', element: page(IngestionPage, '/ingestion') },
      { path: 'approvals', element: page(ApprovalsPage, '/approvals') },
      { path: 'desk', element: page(DeskPage, '/desk') },
      { path: 'feedback', element: page(FeedbackPage, '/feedback') },
      { path: 'audit', element: page(AuditPage, '/audit') },
      { path: 'subscribers', element: page(SubscribersPage, '/subscribers') },
      { path: 'my-lists', element: page(MyListsPage, '/my-lists') },
      { path: 'users', element: page(UsersPage, '/users') },
      { path: 'users/:userId', element: page(RmListsPage, '/users') },
      { path: '*', element: <RouteError notFound /> },
    ],
  },
]);
